import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import RemoveIcon from "@mui/icons-material/Remove";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import LinkCodeActionButton from "component/_common/linkCodeGuide/LinkCodeActionButton";
import {
  captureLinkCodeFromUrl,
  clampWidgetPosition,
  clearLinkCode,
  getDefaultWidgetPosition,
  getLinkCodeButtonNavigatePath,
  getLinkCodeWidgetExpandedHeight,
  getLinkCodeWidgetWidth,
  getMinimizedWidgetPosition,
  getVisibleLinkCodeWidgetButtons,
  hasActiveLinkCode,
  markLinkCodeWidgetIntroShown,
  shouldMinimizeLinkCodeWidget,
  LINK_CODE_CLEARED_EVENT,
  LINK_CODE_UPDATED_EVENT,
  LINK_CODE_WIDGET_MINIMIZED_SIZE,
  LINK_CODE_WIDGET_WIDTH,
  LinkCodeWidgetPosition,
  LinkCodeWidgetSize,
  saveWidgetPosition,
  stripLinkCodeFromUrl,
} from "functions/linkCode";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { RootState } from "state/store";

const WIDGET_Z_INDEX = 1350;

const getExpandedWidgetSize = (buttonCount: number): LinkCodeWidgetSize => ({
  width: getLinkCodeWidgetWidth(),
  height: getLinkCodeWidgetExpandedHeight(buttonCount),
});

const getMeasuredWidgetSize = (
  element: HTMLElement | null,
  fallback: LinkCodeWidgetSize,
): LinkCodeWidgetSize => {
  if (!element) return fallback;

  const rect = element.getBoundingClientRect();
  return {
    width: rect.width || fallback.width,
    height: rect.height || fallback.height,
  };
};

const getInitialWidgetState = (buttonCount: number) => {
  const active = hasActiveLinkCode();
  const minimized = active && shouldMinimizeLinkCodeWidget();

  return {
    isActive: active,
    minimized,
    position: minimized
      ? getMinimizedWidgetPosition()
      : getDefaultWidgetPosition(getExpandedWidgetSize(buttonCount)),
  };
};

const LinkCodeGuide: React.FC = () => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const widgetRef = useRef<HTMLDivElement>(null);
  const expandedPositionRef = useRef<LinkCodeWidgetPosition | null>(null);
  const expandedLandingPathRef = useRef<string | null>(null);
  const previousPathnameRef = useRef(pathname);
  const { isLoggedIn } = useSelector((state: RootState) => state.user);

  const visibleButtons = useMemo(
    () => getVisibleLinkCodeWidgetButtons(isLoggedIn),
    [isLoggedIn],
  );
  const initialWidgetState = useMemo(
    () => getInitialWidgetState(visibleButtons.length),
    [visibleButtons.length],
  );
  const [isActive, setIsActive] = useState(initialWidgetState.isActive);
  const [minimized, setMinimized] = useState(initialWidgetState.minimized);
  const expandedWidgetSize = useMemo(
    () => getExpandedWidgetSize(visibleButtons.length),
    [visibleButtons.length],
  );

  const [position, setPosition] = useState<LinkCodeWidgetPosition>(
    initialWidgetState.position,
  );

  const dragStateRef = useRef({
    isDragging: false,
    pointerId: -1,
    offsetX: 0,
    offsetY: 0,
  });

  const syncActiveState = useCallback(() => {
    const active = hasActiveLinkCode();
    setIsActive(active);

    if (!active) {
      setMinimized(false);
    }
  }, []);

  useEffect(() => {
    syncActiveState();
    window.addEventListener(LINK_CODE_UPDATED_EVENT, syncActiveState);
    window.addEventListener(LINK_CODE_CLEARED_EVENT, syncActiveState);

    return () => {
      window.removeEventListener(LINK_CODE_UPDATED_EVENT, syncActiveState);
      window.removeEventListener(LINK_CODE_CLEARED_EVENT, syncActiveState);
    };
  }, [syncActiveState]);

  useEffect(() => {
    const params = new URLSearchParams(search);
    if (!params.has("linkCode")) return;

    const { captured, openExpanded } = captureLinkCodeFromUrl(search);
    stripLinkCodeFromUrl();

    if (!captured) return;

    setIsActive(true);
    setMinimized(!openExpanded);
    expandedLandingPathRef.current = openExpanded ? pathname : null;
    expandedPositionRef.current = null;
    setPosition(
      openExpanded
        ? getDefaultWidgetPosition(getExpandedWidgetSize(visibleButtons.length))
        : getMinimizedWidgetPosition(),
    );
  }, [search, visibleButtons.length]);

  useEffect(() => {
    if (!isActive) return;
    if (previousPathnameRef.current === pathname) return;

    previousPathnameRef.current = pathname;

    if (expandedLandingPathRef.current === pathname) return;

    expandedLandingPathRef.current = null;
    setPosition((current) => {
      expandedPositionRef.current = current;
      return getMinimizedWidgetPosition();
    });
    setMinimized(true);
    markLinkCodeWidgetIntroShown();
  }, [pathname, isActive]);

  const clampWidgetInsideViewport = useCallback(
    (current: LinkCodeWidgetPosition) => {
      const fallbackSize = minimized
        ? {
            width: LINK_CODE_WIDGET_MINIMIZED_SIZE,
            height: LINK_CODE_WIDGET_MINIMIZED_SIZE,
          }
        : getExpandedWidgetSize(visibleButtons.length);
      const measuredSize = getMeasuredWidgetSize(widgetRef.current, fallbackSize);
      const clamped = clampWidgetPosition(current, measuredSize);

      saveWidgetPosition(clamped);
      if (!minimized) {
        expandedPositionRef.current = clamped;
      }

      return clamped;
    },
    [minimized, visibleButtons.length],
  );

  useLayoutEffect(() => {
    if (!isActive) return;

    setPosition((current) => clampWidgetInsideViewport(current));
  }, [
    isActive,
    minimized,
    visibleButtons.length,
    isLoggedIn,
    clampWidgetInsideViewport,
  ]);

  useEffect(() => {
    const handleViewportChange = () => {
      setPosition((current) => clampWidgetInsideViewport(current));
    };

    window.addEventListener("resize", handleViewportChange);
    window.visualViewport?.addEventListener("resize", handleViewportChange);
    window.visualViewport?.addEventListener("scroll", handleViewportChange);

    return () => {
      window.removeEventListener("resize", handleViewportChange);
      window.visualViewport?.removeEventListener("resize", handleViewportChange);
      window.visualViewport?.removeEventListener("scroll", handleViewportChange);
    };
  }, [clampWidgetInsideViewport]);

  const minimizeWidget = useCallback(() => {
    setPosition((current) => {
      expandedPositionRef.current = current;
      return getMinimizedWidgetPosition();
    });
    setMinimized(true);
    markLinkCodeWidgetIntroShown();
  }, []);

  const handleMinimize = () => {
    minimizeWidget();
  };

  const handleExpand = () => {
    if (!minimized) return;

    const nextPosition =
      expandedPositionRef.current ||
      getDefaultWidgetPosition(getExpandedWidgetSize(visibleButtons.length));
    setPosition(nextPosition);
    saveWidgetPosition(nextPosition);
    setMinimized(false);
  };

  const handleCancel = () => {
    setMinimized(false);
    expandedPositionRef.current = null;
    clearLinkCode();
    stripLinkCodeFromUrl();
    setIsActive(false);
  };

  const handleNavigate = (path: string) => {
    stripLinkCodeFromUrl();
    navigate(path);
  };

  const handleDragPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!widgetRef.current || minimized) return;

    event.stopPropagation();
    const rect = widgetRef.current.getBoundingClientRect();
    dragStateRef.current = {
      isDragging: true,
      pointerId: event.pointerId,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
    };

    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleDragPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const dragState = dragStateRef.current;
    if (
      minimized ||
      !dragState.isDragging ||
      dragState.pointerId !== event.pointerId
    ) {
      return;
    }

    const nextPosition = clampWidgetPosition(
      {
        x: event.clientX - dragState.offsetX,
        y: event.clientY - dragState.offsetY,
      },
      expandedWidgetSize,
    );

    setPosition(nextPosition);
    expandedPositionRef.current = nextPosition;
  };

  const finishDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    const dragState = dragStateRef.current;
    if (!dragState.isDragging || dragState.pointerId !== event.pointerId) {
      return;
    }

    dragState.isDragging = false;

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    setPosition((current) => clampWidgetInsideViewport(current));

    dragState.pointerId = -1;
  };

  if (!isActive) return null;

  if (minimized) {
    return (
      <Box
        role="button"
        aria-label="Open referral link guide"
        tabIndex={0}
        onClick={handleExpand}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleExpand();
          }
        }}
        sx={{
          position: "fixed",
          left: position.x,
          top: position.y,
          width: LINK_CODE_WIDGET_MINIMIZED_SIZE,
          height: LINK_CODE_WIDGET_MINIMIZED_SIZE,
          zIndex: WIDGET_Z_INDEX,
          borderRadius: "50%",
          bgcolor: "primary.main",
          color: "#1f1f1f",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
          cursor: "pointer",
          transition: "transform 0.2s ease, box-shadow 0.2s ease",
          "&:hover": {
            transform: "scale(1.04)",
            boxShadow: "0 10px 28px rgba(0, 0, 0, 0.24)",
          },
        }}
      >
        <CampaignOutlinedIcon sx={{ fontSize: 24 }} />
      </Box>
    );
  }

  return (
    <Box
      ref={widgetRef}
      role="complementary"
      aria-label="Referral link guide"
      sx={{
        position: "fixed",
        left: position.x,
        top: position.y,
        width: expandedWidgetSize.width,
        maxWidth: "calc(100vw - 32px)",
        maxHeight: "calc(100dvh - 48px)",
        zIndex: WIDGET_Z_INDEX,
        borderRadius: "14px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        boxShadow: "0 12px 32px rgba(0, 0, 0, 0.18)",
        bgcolor: "#fff",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          px: 1,
          py: 0.75,
          bgcolor: "primary.main",
          color: "#1f1f1f",
        }}
      >
        <Box
          onPointerDown={handleDragPointerDown}
          onPointerMove={handleDragPointerMove}
          onPointerUp={finishDrag}
          onPointerCancel={finishDrag}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "grab",
            touchAction: "none",
            color: "rgba(0,0,0,0.65)",
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>

        <CampaignOutlinedIcon sx={{ fontSize: 18, color: "rgba(0,0,0,0.75)" }} />

        <Typography
          className="fm-poppins"
          sx={{
            flex: 1,
            fontWeight: 700,
            fontSize: "0.95rem",
            lineHeight: 1.2,
            color: "#1f1f1f",
          }}
        >
          Referral link active
        </Typography>

        <IconButton
          aria-label="Minimize referral guide"
          size="small"
          onClick={handleMinimize}
          sx={{ color: "rgba(0,0,0,0.75)" }}
        >
          <RemoveIcon fontSize="small" />
        </IconButton>

        <IconButton
          aria-label="Close referral link"
          size="small"
          onClick={handleCancel}
          sx={{ color: "rgba(0,0,0,0.75)" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box px={2.25} pt={2} pb={2} sx={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
        <Typography
          className="fm-poppins"
          sx={{ fontSize: "0.95rem", color: "#2f2f2f", mb: 1.25 }}
        >
          Welcome! Your referral is active for this session.
        </Typography>

        <Typography
          className="fm-poppins"
          sx={{ fontSize: "0.9rem", color: "#5f5f5f", lineHeight: 1.45, mb: 2 }}
        >
          Choose a service below to apply your referral benefit.
          {!isLoggedIn
            ? " Sign in first if you need a personalized experience."
            : ""}
        </Typography>

        <Typography
          className="fm-poppins"
          sx={{
            fontSize: "0.68rem",
            fontWeight: 700,
            letterSpacing: "0.08em",
            color: "#8a8a8a",
            mb: 1,
          }}
        >
          APPLY REFERRAL
        </Typography>

        {visibleButtons.map((button) => (
          <LinkCodeActionButton
            key={button.id}
            button={button}
            isActive={pathname === button.redirectPath}
            onClick={() =>
              handleNavigate(getLinkCodeButtonNavigatePath(button, isLoggedIn))
            }
          />
        ))}

        <Box mt={1.75} textAlign="center">
          <Typography
            component="button"
            type="button"
            onClick={handleCancel}
            className="fm-poppins"
            sx={{
              border: "none",
              background: "none",
              p: 0,
              cursor: "pointer",
              fontSize: "0.9rem",
              color: "#6d6d6d",
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            Cancel referral
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default LinkCodeGuide;
